import { useEffect, useState, type FormEvent } from "react";
import { Card } from "@tremor/react";
import {
  Globe, Plus, Tag, Users, Server, Edit3, Power, Check, Play, Loader2,
  Link as LinkIcon, Trash2, RotateCw, Terminal, Copy, X, Search,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useProperties, useCreateProperty, useUpdateProperty, useDeactivateProperty,
  useTriggerAudit,
  useGoogleOAuthStatus, useStartGoogleOAuth, useDisconnectGoogleOAuth,
  useSSHStatus, useStartSSH, useTestSSH, useDisconnectSSH,
  type Property, type CreatePropertyBody, type SSHStartResponse, type SSHTestResponse,
} from "@/lib/queries";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

export default function Properties() {
  const list = useProperties();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Property | null>(null);

  const items = list.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Properties</h1>
          <p className="text-sm text-muted-foreground">
            Websites and brands Auriel audits. Each property carries its
            target keywords, competitor list, and (optionally) per-source
            credentials so audits can pull all wired data sources at once.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setCreating(true); setEditing(null); }}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Add property
        </button>
      </div>

      {creating && (
        <PropertyForm
          onCancel={() => setCreating(false)}
          onSaved={() => setCreating(false)}
        />
      )}

      {editing && (
        <PropertyForm
          existing={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}

      {list.isLoading && (
        <div className="text-sm text-muted-foreground">Loading…</div>
      )}

      {!list.isLoading && items.length === 0 && (
        <Card>
          <div className="flex flex-col items-center gap-3 p-8 text-center">
            <Globe className="h-10 w-10 text-muted-foreground/60" />
            <div>
              <h3 className="text-base font-semibold">No properties yet</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Add your first website (e.g. <code>nrg.ro</code>, <code>nws.ro</code>)
                to start auditing it.
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {items.map((p) => (
          <PropertyCard
            key={p.id}
            property={p}
            onEdit={() => { setEditing(p); setCreating(false); }}
          />
        ))}
      </div>
    </div>
  );
}


function PropertyCard({ property: p, onEdit }: { property: Property; onEdit: () => void }) {
  const deactivate = useDeactivateProperty();
  const trigger = useTriggerAudit();
  const nav = useNavigate();
  const wiredSources: string[] = [];
  if (p.ahrefs_project_id !== null && p.ahrefs_project_id !== "") wiredSources.push("Ahrefs");
  if (p.semrush_project_id !== null && p.semrush_project_id !== "") wiredSources.push("SEMrush");
  if (p.gsc_property_id !== null && p.gsc_property_id !== "") wiredSources.push("GSC");
  if (p.ga4_property_id !== null && p.ga4_property_id !== "") wiredSources.push("GA4");
  if (p.google_ads_customer_id !== null && p.google_ads_customer_id !== "") wiredSources.push("Ads");
  if (p.ssh_host !== null && p.ssh_user !== null) wiredSources.push("Server SSH");

  return (
    <Card className={cn(!p.is_active && "opacity-60")}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 shrink-0 text-primary" />
            <a
              href={`https://${p.domain}`}
              target="_blank" rel="noopener noreferrer"
              className="truncate font-mono text-sm font-medium hover:text-primary hover:underline"
            >
              {p.domain}
            </a>
            {!p.is_active && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                inactive
              </span>
            )}
          </div>
          <div className="mt-1 truncate text-sm text-muted-foreground">{p.display_name}</div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {p.is_active && (
            <button
              type="button"
              onClick={() => nav(`/p/${p.id}/overview`)}
              title="Open property workspace"
              className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary/5 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"
            >
              <Search className="h-3 w-3" />
              Open
            </button>
          )}
          {p.is_active && (
            <button
              type="button"
              onClick={async () => {
                try {
                  await trigger.mutateAsync(p.id);
                  nav("/audits");
                } catch (e) {
                  // 429 = in-flight audit already exists — friendlier message
                  if (e instanceof Error && e.message.includes("429")) {
                    alert("An audit for this property is already running. Check the Audits tab.");
                    nav("/audits");
                  } else {
                    alert(`Could not start audit: ${e instanceof Error ? e.message : "unknown error"}`);
                  }
                }
              }}
              disabled={trigger.isPending}
              title="Run audit"
              className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary/10 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/20 disabled:opacity-50"
            >
              {trigger.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
              Audit
            </button>
          )}
          <button
            type="button"
            onClick={onEdit}
            title="Edit"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <Edit3 className="h-3.5 w-3.5" />
          </button>
          {p.is_active && (
            <button
              type="button"
              onClick={async () => {
                if (!confirm(`Deactivate ${p.domain}? Audits will stop running. Source data history is preserved.`)) return;
                await deactivate.mutateAsync(p.id);
              }}
              title="Deactivate"
              className="rounded-md p-1.5 text-muted-foreground hover:bg-red-500/10 hover:text-red-600"
            >
              <Power className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <div className="space-y-1.5">
          <Stat icon={Tag} label="Keywords" value={p.target_keywords.length} />
          <Stat icon={Users} label="Competitors" value={p.competitor_domains.length} />
          <Stat
            icon={Server}
            label="Server SSH"
            value={p.ssh_host ? `${p.ssh_user || "?"}@${p.ssh_host}` : "—"}
          />
        </div>
        <div className="space-y-1.5">
          <Stat label="Sources wired" value={wiredSources.length > 0 ? wiredSources.join(", ") : "none"} />
          <Stat label="Audit cron" value={p.audit_cron} mono />
        </div>
      </div>

      {p.target_keywords.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-xs font-medium text-muted-foreground">Target keywords</div>
          <div className="flex flex-wrap gap-1">
            {p.target_keywords.slice(0, 8).map((kw) => (
              <span key={kw} className="rounded-md border border-border bg-background px-2 py-0.5 text-xs">
                {kw}
              </span>
            ))}
            {p.target_keywords.length > 8 && (
              <span className="text-xs text-muted-foreground">+{p.target_keywords.length - 8} more</span>
            )}
          </div>
        </div>
      )}

      {p.is_active && <GoogleConnectStrip propertyId={p.id} />}
      {p.is_active && <SSHConnectStrip propertyId={p.id} />}
    </Card>
  );
}


// ─── Google connect strip (per property) ─────────────────────────────

function GoogleConnectStrip({ propertyId }: { propertyId: number }) {
  const status = useGoogleOAuthStatus(propertyId);
  const start = useStartGoogleOAuth();
  const disconnect = useDisconnectGoogleOAuth();

  // Catch the ?google_oauth=connected redirect-back banner. We strip
  // the query string so it doesn't linger on subsequent navigations,
  // and we only show the banner on the matching property card.
  const [banner, setBanner] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const result = sp.get("google_oauth");
    const justConnectedPid = Number(sp.get("property_id") || 0);
    if (result === "connected" && justConnectedPid === propertyId) {
      setBanner({
        kind: "ok",
        text: `Connected: ${sp.get("account") || "Google account"}`,
      });
      sp.delete("google_oauth"); sp.delete("account"); sp.delete("property_id");
      const q = sp.toString();
      window.history.replaceState({}, "", window.location.pathname + (q ? "?" + q : ""));
    } else if (result === "denied" && justConnectedPid === propertyId) {
      setBanner({ kind: "error", text: "Connection cancelled at Google" });
      sp.delete("google_oauth"); sp.delete("reason"); sp.delete("property_id");
      const q = sp.toString();
      window.history.replaceState({}, "", window.location.pathname + (q ? "?" + q : ""));
    }
  }, [propertyId]);

  if (status.isLoading) return null;
  const s = status.data;

  const onConnect = async () => {
    try {
      const r = await start.mutateAsync({ property_id: propertyId });
      window.location.href = r.auth_url;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "failed";
      setBanner({ kind: "error", text: `Couldn't start OAuth: ${msg}` });
    }
  };
  const onDisconnect = async () => {
    if (!confirm("Disconnect Google for this property? Auriel will lose GSC + GA4 access for it.")) return;
    try {
      await disconnect.mutateAsync(propertyId);
      await status.refetch();
      setBanner({ kind: "ok", text: "Disconnected — Google grant revoked." });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "failed";
      setBanner({ kind: "error", text: `Disconnect failed: ${msg}` });
    }
  };

  return (
    <div className="mt-3 rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2 text-xs">
          <span className="font-medium text-muted-foreground">Google:</span>
          {s?.connected ? (
            <span className="inline-flex items-center gap-1 truncate text-emerald-700 dark:text-emerald-400">
              <Check className="h-3 w-3 shrink-0" />
              connected as <strong className="truncate">{s.account_email || "?"}</strong>
            </span>
          ) : s?.has_oauth_client ? (
            <span className="text-muted-foreground">not connected</span>
          ) : (
            <span className="text-amber-700 dark:text-amber-400">
              OAuth client not configured (admin: Settings → Secrets)
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {s?.connected ? (
            <>
              <button
                type="button"
                onClick={onConnect}
                disabled={!s.has_oauth_client || start.isPending}
                title="Re-authorize (different account, or refresh scopes)"
                className="rounded-md border border-border bg-background px-2 py-0.5 text-[11px] hover:bg-accent disabled:opacity-50"
              >
                <RotateCw className={cn("inline h-3 w-3", start.isPending && "animate-spin")} />
              </button>
              <button
                type="button"
                onClick={onDisconnect}
                disabled={disconnect.isPending}
                title="Disconnect"
                className="rounded-md border border-red-500/40 bg-background px-2 py-0.5 text-[11px] text-red-600 hover:bg-red-500/10 disabled:opacity-50 dark:text-red-400"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onConnect}
              disabled={!s?.has_oauth_client || start.isPending}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-0.5 text-[11px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {start.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <LinkIcon className="h-3 w-3" />}
              Connect
            </button>
          )}
        </div>
      </div>
      {banner && (
        <div className={cn(
          "mt-2 rounded px-2 py-1 text-[11px]",
          banner.kind === "ok"
            ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
            : "bg-red-500/10 text-red-700 dark:text-red-400",
        )}>{banner.text}</div>
      )}
    </div>
  );
}


// ─── Server SSH connect strip (per property) ─────────────────────────

function SSHConnectStrip({ propertyId }: { propertyId: number }) {
  const status = useSSHStatus(propertyId);
  const start = useStartSSH();
  const test = useTestSSH();
  const disconnect = useDisconnectSSH();

  // The connect dialog opens inline below the strip when the user
  // clicks Connect or Reissue key. Holds host/user/port + the
  // returned snippet so they can copy it.
  const [open, setOpen] = useState(false);
  const [host, setHost] = useState("");
  const [sshUser, setSshUser] = useState("");
  const [port, setPort] = useState(22);
  const [snippet, setSnippet] = useState<string | null>(null);
  const [fp, setFp] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<SSHTestResponse | null>(null);

  const s = status.data;

  // Pre-fill from existing connection so the user doesn't have to retype
  // when re-issuing a key. MUST run before the early return below — React
  // hooks must be called in the same order every render.
  useEffect(() => {
    if (s?.host && !host) setHost(s.host);
    if (s?.user && !sshUser) setSshUser(s.user);
    if (s?.port && port === 22) setPort(s.port);
  }, [s?.host, s?.user, s?.port]); // eslint-disable-line react-hooks/exhaustive-deps

  if (status.isLoading) return null;

  const onStart = async () => {
    setError(null);
    setTestResult(null);
    try {
      const r: SSHStartResponse = await start.mutateAsync({
        property_id: propertyId,
        host: host.trim(),
        user: sshUser.trim(),
        port,
      });
      setSnippet(r.install_snippet);
      setFp(r.fingerprint);
      setOpen(true);
      await status.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to mint key");
    }
  };

  const onTest = async () => {
    setError(null);
    setTestResult(null);
    try {
      const r = await test.mutateAsync(propertyId);
      setTestResult(r);
      if (!r.ok) setError(r.error || "test failed");
    } catch (e) {
      setError(e instanceof Error ? e.message : "test failed");
    }
  };

  const onDisconnect = async () => {
    if (!confirm(
      "Disconnect SSH for this property? Auriel will lose access-log "
      + "visibility for it.\n\nThe public key will remain in your server's "
      + "authorized_keys until you remove it manually — but with the private "
      + "half gone, it's harmless."
    )) return;
    try {
      await disconnect.mutateAsync(propertyId);
      setSnippet(null);
      setFp(null);
      setOpen(false);
      setTestResult(null);
      setHost(""); setSshUser(""); setPort(22);
      await status.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : "disconnect failed");
    }
  };

  const onCopy = async () => {
    if (!snippet) return;
    try { await navigator.clipboard.writeText(snippet); }
    catch { /* clipboard may be blocked — user can select manually */ }
  };

  const pill = s?.connected ? (
    s.last_test_ok === true ? (
      <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
        <Check className="h-3 w-3 shrink-0" />
        connected: <strong className="truncate font-mono">{s.user}@{s.host}{s.port !== 22 ? `:${s.port}` : ""}</strong>
      </span>
    ) : s.last_test_ok === false ? (
      <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
        <X className="h-3 w-3 shrink-0" />
        last test failed: <span className="truncate font-mono">{s.user}@{s.host}</span>
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
        key issued but never tested: <span className="truncate font-mono">{s.user}@{s.host}</span>
      </span>
    )
  ) : (
    <span className="text-muted-foreground">not connected</span>
  );

  return (
    <div className="mt-3 rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2 text-xs">
          <span className="font-medium text-muted-foreground">Server SSH:</span>
          {pill}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {s?.connected ? (
            <>
              <button
                type="button"
                onClick={onTest}
                disabled={test.isPending}
                title="Test connection (re-probes the server)"
                className="rounded-md border border-border bg-background px-2 py-0.5 text-[11px] hover:bg-accent disabled:opacity-50"
              >
                {test.isPending ? <Loader2 className="inline h-3 w-3 animate-spin" /> : "Test"}
              </button>
              <button
                type="button"
                onClick={() => { setOpen((v) => !v); setError(null); }}
                title="Re-issue key (rotates to a fresh keypair)"
                className="rounded-md border border-border bg-background px-2 py-0.5 text-[11px] hover:bg-accent"
              >
                <RotateCw className="inline h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={onDisconnect}
                disabled={disconnect.isPending}
                title="Disconnect"
                className="rounded-md border border-red-500/40 bg-background px-2 py-0.5 text-[11px] text-red-600 hover:bg-red-500/10 disabled:opacity-50 dark:text-red-400"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => { setOpen((v) => !v); setError(null); }}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-0.5 text-[11px] font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Terminal className="h-3 w-3" />
              Connect server
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="mt-2 space-y-2 rounded-md border border-border bg-background p-2">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_80px_auto]">
            <input
              type="text"
              placeholder="host (e.g. nrg.ro or 195.x.y.z)"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              className="rounded-md border border-input bg-background px-2 py-1 font-mono text-xs"
            />
            <input
              type="text"
              placeholder="user (cPanel account name)"
              value={sshUser}
              onChange={(e) => setSshUser(e.target.value)}
              className="rounded-md border border-input bg-background px-2 py-1 font-mono text-xs"
            />
            <input
              type="number"
              value={port}
              onChange={(e) => setPort(parseInt(e.target.value || "22", 10))}
              min={1} max={65535}
              className="rounded-md border border-input bg-background px-2 py-1 font-mono text-xs"
            />
            <button
              type="button"
              onClick={onStart}
              disabled={!host.trim() || !sshUser.trim() || start.isPending}
              className="inline-flex items-center justify-center gap-1 rounded-md bg-primary px-2 py-1 text-[11px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {start.isPending
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : (s?.connected ? "Reissue key" : "Generate key")}
            </button>
          </div>

          {snippet && (
            <div className="rounded-md border border-border bg-muted/40 p-2">
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="text-[11px] text-muted-foreground">
                  Paste this on <span className="font-mono">{host}</span> as user
                  {" "}<span className="font-mono">{sshUser}</span>:
                </span>
                <button
                  type="button"
                  onClick={onCopy}
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5 text-[11px] hover:bg-accent"
                >
                  <Copy className="h-3 w-3" /> Copy
                </button>
              </div>
              <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-all rounded bg-background p-2 font-mono text-[10px] leading-relaxed">{snippet}</pre>
              {fp && (
                <p className="mt-1 text-[10px] text-muted-foreground">
                  fingerprint: <span className="font-mono">{fp}</span>
                </p>
              )}
              <p className="mt-1 text-[11px] text-muted-foreground">
                After pasting the snippet on your server, click <strong>Test</strong> above to verify Auriel can read your access logs.
              </p>
            </div>
          )}

          {testResult?.ok && (
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-2 text-[11px]">
              <div className="font-medium text-emerald-700 dark:text-emerald-400">
                ✓ Connection works.
              </div>
              <div className="mt-1 space-y-0.5 text-foreground/80">
                <div>whoami: <span className="font-mono">{testResult.whoami}</span></div>
                <div>uname: <span className="font-mono">{testResult.uname}</span></div>
                {testResult.chosen_log_path && (
                  <div>access log: <span className="font-mono">{testResult.chosen_log_path}</span></div>
                )}
                {!testResult.chosen_log_path && (
                  <div className="text-amber-700 dark:text-amber-400">
                    Connected, but no access log was found at any standard path.
                    Audits won't have log data until logs become readable to this user.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-2 rounded px-2 py-1 text-[11px] bg-red-500/10 text-red-700 dark:text-red-400">
          {error}
        </div>
      )}
    </div>
  );
}


function Stat({ icon: Icon, label, value, mono }: {
  icon?: typeof Tag; label: string; value: string | number | null; mono?: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      <span className="text-muted-foreground">{label}:</span>
      <span className={cn("truncate", mono && "font-mono")}>{value ?? "—"}</span>
    </div>
  );
}


// ─── Property create/edit form ───────────────────────────────────────

function PropertyForm({
  existing, onCancel, onSaved,
}: {
  existing?: Property;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const create = useCreateProperty();
  const update = useUpdateProperty();
  const isEditing = existing !== undefined;

  const [domain, setDomain] = useState(existing?.domain ?? "");
  const [displayName, setDisplayName] = useState(existing?.display_name ?? "");
  const [ahrefsId, setAhrefsId] = useState(existing?.ahrefs_project_id ?? "");
  const [gscId, setGscId] = useState(existing?.gsc_property_id ?? "");
  const [ga4Id, setGa4Id] = useState(existing?.ga4_property_id ?? "");
  // cpanel_host / cpanel_user removed — server SSH access is now self-
  // serve from the property card via SSHConnectStrip.
  const [keywords, setKeywords] = useState((existing?.target_keywords ?? []).join("\n"));
  const [competitors, setCompetitors] = useState((existing?.competitor_domains ?? []).join("\n"));
  const [brandTerms, setBrandTerms] = useState((existing?.brand_terms ?? []).join("\n"));
  const [auditCron, setAuditCron] = useState(existing?.audit_cron ?? "0 8 * * 1");
  const [error, setError] = useState<string | null>(null);

  const isPending = create.isPending || update.isPending;
  const orNull = (s: string) => (s.trim() === "" ? null : s.trim());
  const splitLines = (s: string) =>
    s.split(/[\n,]+/).map((x) => x.trim()).filter(Boolean);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (isEditing && existing) {
        await update.mutateAsync({
          id: existing.id,
          body: {
            display_name: displayName,
            ahrefs_project_id: orNull(ahrefsId),
            gsc_property_id: orNull(gscId),
            ga4_property_id: orNull(ga4Id),
            target_keywords: splitLines(keywords),
            competitor_domains: splitLines(competitors),
            brand_terms: splitLines(brandTerms),
            audit_cron: auditCron,
          },
        });
      } else {
        const body: CreatePropertyBody = {
          domain,
          display_name: displayName,
          ahrefs_project_id: orNull(ahrefsId),
          gsc_property_id: orNull(gscId),
          ga4_property_id: orNull(ga4Id),
          target_keywords: splitLines(keywords),
          competitor_domains: splitLines(competitors),
          brand_terms: splitLines(brandTerms),
          audit_cron: auditCron,
        };
        await create.mutateAsync(body);
      }
      onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setError("A property with that domain already exists in this tenant.");
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError("Save failed.");
      }
    }
  }

  return (
    <Card>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">
            {isEditing ? `Edit ${existing.domain}` : "New property"}
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || (!isEditing && !domain.trim()) || !displayName.trim()}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? "Saving…" : (isEditing ? <><Check className="h-4 w-4" /> Save</> : <><Plus className="h-4 w-4" /> Create</>)}
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {!isEditing && (
            <Field label="Domain" placeholder="example.com" value={domain} onChange={setDomain} required mono />
          )}
          <Field label="Display name" placeholder="My Marketing Site" value={displayName} onChange={setDisplayName} required />
        </div>

        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Per-source selectors (optional)</h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Ahrefs project ID" value={ahrefsId} onChange={setAhrefsId} mono />
            <Field label="GSC property ID" placeholder="sc-domain:example.com" value={gscId} onChange={setGscId} mono />
            <Field label="GA4 property ID" placeholder="123456789" value={ga4Id} onChange={setGa4Id} mono />
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Server log access is configured per-property after creation —
            use <strong>Connect server</strong> on the property card.
          </p>
        </div>

        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Work definition</h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <TextArea
              label="Target keywords"
              placeholder="one per line"
              value={keywords} onChange={setKeywords} rows={6}
              hint={`${splitLines(keywords).length} keyword${splitLines(keywords).length === 1 ? "" : "s"}`}
            />
            <TextArea
              label="Competitor domains"
              placeholder="one per line"
              value={competitors} onChange={setCompetitors} rows={6}
              hint={`${splitLines(competitors).length} competitor${splitLines(competitors).length === 1 ? "" : "s"}`}
            />
            <TextArea
              label="Brand terms"
              placeholder="for AI-search Brand Radar"
              value={brandTerms} onChange={setBrandTerms} rows={6}
              hint={`${splitLines(brandTerms).length} term${splitLines(brandTerms).length === 1 ? "" : "s"}`}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Audit cron (UTC)" value={auditCron} onChange={setAuditCron} mono required hint="Default: weekly Mon 08:00 UTC" />
        </div>
      </form>
    </Card>
  );
}


function Field({ label, value, onChange, type = "text", placeholder, required, mono, hint }: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string; required?: boolean; mono?: boolean; hint?: string;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium">{label}{required && <span className="text-red-500"> *</span>}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className={cn(
          "mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          mono && "font-mono",
        )}
      />
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </label>
  );
}


function TextArea({ label, value, onChange, placeholder, rows, hint }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; rows?: number; hint?: string;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows ?? 4}
        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </label>
  );
}
